// CRM 2.0 · M12 Template Builder step 4 · Check & preview: "Preview with booking". Legal picks a booking from a list
// (the template's linked projects, or a search by unit no., customer name, tower or project; no Allotment Payment ID
// needed); the template prints with that booking's real values exactly as the final print would (buyer block per
// applicant, the booking's payment plan, page rules, page fixes, sample QRs). A field with no value prints
// highlighted "— missing —". Under the PDF the check panel: Missing (with links to their pages), Used (the value
// printed, to compare with the Sales App), the page count and where each New page line landed.
// Read only: nothing is saved, stored or logged as a print. The last 5 IDs are kept in this browser.
import React, { useEffect, useRef, useState } from "react";
import cls from "./Templates.module.css";
import { getBookingPreview, getPreviewBookings } from "../../Services/Templates";
import { errorTost } from "../../Services/Toast";

const RECENT_KEY = "crm2.templates.recentBookings";
const readRecent = () => {
  try { return JSON.parse(window.localStorage.getItem(RECENT_KEY) || "[]").slice(0, 5); } catch (e) { return []; }
};
const saveRecent = (list) => {
  try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5))); } catch (e) { /* private window: not kept */ }
};

function Pages({ pages, onPage }) {
  if (!pages || !pages.length) return <span className={cls.muted}>—</span>;
  return pages.map((p) => <button key={p} type="button" className={cls.link} style={{ marginRight: ".6rem" }} onClick={() => onPage(p)}>p {p}</button>);
}

const bookingLabel = (b) => [b.project, b.tower || b.wing, b.unitNo].filter(Boolean).join(" · ");

// The bookings to pick from: no search = the ones this template is used for; a search = any project
function BookingPicker({ versionId, chosenId, working, onPick }) {
  const [search, setSearch] = useState("");
  const [list, setList] = useState(null); // { scope, bookings }
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    const q = search.trim();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await getPreviewBookings(versionId, q);
        if (live) setList(r);
      } catch (e) {
        if (live) errorTost(e.message);
      } finally {
        if (live) setLoading(false);
      }
    }, q ? 350 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [versionId, search]);

  const scope = list && list.scope;
  const count = list ? list.bookings.length : 0;
  const scopeText = !scope ? "" : scope.kind === "LINKED" ? `Bookings in ${scope.projects.join(", ")}, where this template is used`
    : scope.kind === "ALL" ? scope.why : `${count} booking${count === 1 ? "" : "s"} found for “${scope.search}”`;

  return (
    <div>
      <div className={cls.actionbar} style={{ gap: ".8rem", marginBottom: ".8rem" }}>
        <label className={cls.lbl} htmlFor="bookingSearch" style={{ margin: 0 }}>Find a booking</label>
        <input id="bookingSearch" className={cls.optInp} style={{ flex: "1 1 32rem" }} value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Unit no., customer name, tower or project" autoComplete="off" spellCheck={false} />
      </div>
      <p className={cls.hint} style={{ margin: "0 0 .8rem" }}>{loading ? "Looking for bookings…" : scopeText}</p>
      {list && count === 0 && !loading && <p className={cls.hint}>No booking found. Try a unit number or part of the customer's name.</p>}
      {count > 0 && (
        <div className={cls.tblw} style={{ maxHeight: "36rem", overflowY: "auto" }}>
          <table className={cls.tbl}>
            <thead><tr><th>Booking</th><th>Customer</th><th>Status</th><th className={cls.last} /></tr></thead>
            <tbody>{list.bookings.map((b) => {
              const chosen = b.allotmentPaymentId === chosenId;
              return (
                <tr key={b.allotmentPaymentId}>
                  <td className={cls.main}>{bookingLabel(b) || b.allotmentPaymentId}</td>
                  <td>{b.customer || <span className={cls.muted}>—</span>}</td>
                  <td>
                    <span className={`${cls.chip} ${b.ended ? cls.cErr : cls.cLt}`} style={{ marginRight: ".6rem" }}>{b.status || "No status"}</span>
                    <span className={cls.hint}>{b.crm2 ? "CRM 2.0" : "Old flow"}{b.landowner ? " · Landowner unit" : ""}</span>
                  </td>
                  <td className={cls.last}>
                    {chosen ? <b>Shown below</b> : (
                      <button type="button" className={cls.btn} disabled={working} onClick={() => onPick(b)}>Preview</button>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// onPreviewed(booking): a preview was made (the Approval page unlocks Approve); onTried(): one was tried, made or not
function BookingPreview({ versionId, onPreviewed, onTried }) {
  const [recent, setRecent] = useState(readRecent);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null); // { booking, check, warnings }
  const [url, setUrl] = useState(null);
  const [page, setPage] = useState(1);
  const frameRef = useRef(null);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  async function preview(bookingId) {
    const want = String(bookingId || "").trim();
    if (!want) return;
    setWorking(true);
    try {
      const r = await getBookingPreview(versionId, want);
      const bytes = Uint8Array.from(atob(r.pdf), (c) => c.charCodeAt(0));
      setUrl(URL.createObjectURL(new Blob([bytes], { type: "application/pdf" })));
      setPage(1);
      setResult(r);
      if (onPreviewed) onPreviewed(r.booking);
      const label = bookingLabel(r.booking);
      const list = [{ id: want, label }, ...recent.filter((x) => x.id !== want)].slice(0, 5);
      setRecent(list);
      saveRecent(list);
    } catch (e) {
      errorTost(e.message);
    } finally {
      setWorking(false);
      if (onTried) onTried();
    }
  }

  const goPage = (p) => {
    setPage(p);
    if (frameRef.current) frameRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  const b = result && result.booking;
  const check = result && result.check;
  const warnings = result ? [...(check.warnings || []), ...(result.warnings || [])] : [];

  return (
    <div>
      <p className={cls.hint} style={{ marginTop: 0 }}>
        Pick a booking to see this template filled with that booking's real values, exactly as the final print.
        Read only: nothing is saved, stored or printed.
      </p>
      <BookingPicker versionId={versionId} chosenId={result ? result.booking.allotmentPaymentId : null} working={working} onPick={(b) => preview(b.allotmentPaymentId)} />
      {recent.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".6rem", margin: ".8rem 0 1.2rem" }}>
          <span className={cls.hint} style={{ margin: 0 }}>Recent:</span>
          {recent.map((r) => (
            <button key={r.id} type="button" className={`${cls.chip} ${cls.cLt}`} disabled={working} title={r.id} onClick={() => preview(r.id)}>
              {r.label || `${r.id.slice(0, 8)}…`}
            </button>
          ))}
        </div>
      )}
      {working && <p className={cls.hint}>Reading the booking and making the print… a long agreement takes about half a minute.</p>}

      {result && (
        <>
          <p style={{ margin: "1.2rem 0 .8rem" }}>
            <b>{[b.project, b.wing, b.unitNo].filter(Boolean).join(" · ") || b.allotmentPaymentId}</b>
            {" · "}{b.applicants} applicant{b.applicants === 1 ? "" : "s"} · {b.crm2 ? "CRM 2.0 booking" : "Old-flow booking"}
            {" · "}payment plan: {b.planStages ? `${b.planStages} stages` : "not found"} · <b>{check.pages}</b> pages
          </p>
          {warnings.length > 0 && <div className={cls.sugBox} style={{ marginBottom: "1.2rem" }}>{warnings.map((w) => <div key={w}>{w}</div>)}</div>}
          <iframe key={`${url}#${page}`} ref={frameRef} title="Preview with booking" src={`${url}#page=${page}`}
            style={{ width: "100%", height: "80vh", border: "1px solid var(--g200, #EAECF0)", borderRadius: ".8rem" }} />

          <h3 className={cls.panelTitle} style={{ marginTop: "2rem" }}>
            Missing <span className={cls.count}>· {check.missing.length} · printed as “— missing —”</span>
          </h3>
          {check.missing.length ? (
            <div className={cls.tblw}>
              <table className={cls.tbl}>
                <thead><tr><th>Field</th><th>Where in the PDF</th></tr></thead>
                <tbody>{check.missing.map((m) => <tr key={m.key}><td className={cls.main}>{m.label}</td><td><Pages pages={m.pages} onPage={goPage} /></td></tr>)}</tbody>
              </table>
            </div>
          ) : <p className={cls.ok}>Every field has a value for this booking ✓</p>}

          <h3 className={cls.panelTitle} style={{ marginTop: "2rem" }}>
            Used <span className={cls.count}>· {check.used.length} fields · compare with the Sales App</span>
          </h3>
          <div className={cls.tblw}>
            <table className={cls.tbl}>
              <thead><tr><th>Field</th><th>Printed value</th><th>Where in the PDF</th></tr></thead>
              <tbody>{check.used.map((u) => (
                <tr key={u.key}><td className={cls.main}>{u.label}</td><td style={{ minWidth: "28rem" }}>{u.value}</td><td><Pages pages={u.pages} onPage={goPage} /></td></tr>
              ))}</tbody>
            </table>
          </div>

          <h3 className={cls.panelTitle} style={{ marginTop: "2rem" }}>
            Pages <span className={cls.count}>· {check.pages} pages · where each New page line landed</span>
          </h3>
          {check.newPages.length ? (
            <div className={cls.tblw}>
              <table className={cls.tbl}>
                <thead><tr><th>New page line</th><th>Starts on</th><th>Line</th></tr></thead>
                <tbody>{check.newPages.map((n) => (
                  <tr key={`${n.title}-${n.page}`}><td className={cls.main}>{n.title}</td><td><Pages pages={n.page ? [n.page] : []} onPage={goPage} /></td><td>{n.by}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ) : <p className={cls.hint}>No New page lines in this template.</p>}
        </>
      )}
    </div>
  );
}

export default BookingPreview;
